import React, { useContext, useEffect, useState } from 'react';
import { updateLabelsForSchema, updateProject } from '../../API/Project/project';
import Loader from '../Shared/Loader/loader';
import { Header, PageContainer } from '../Drawer/PageContainer';
import { useTranslation } from 'react-i18next';
import Scrollable from '../Shared/Edit/Scrollable';
import { LanguagesMap } from '../OrganizationSettings/LanguagesMap';
import { OverlayLoader } from '../Shared/Loader/OverlayLoader';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import SaveDialog from '../Shared/Remove/SaveDialog';
import { Dialog } from '@progress/kendo-react-dialogs';
import FailedToLoadError from '../Shared/Recommendation/Error/FailedToLoadError';
import { GlobalContext } from '../../Renderers/GlobalContext';

const AppSettings = ({ projectData, updateProjectData }) => {
    const { subscription } = useContext(GlobalContext).state;
    const [project, setProject] = useState(null);
    const [initialProject, setInitialProject] = useState(null);
    const [language, setLanguage] = useState(null);
    const [schemaLanguages, setSchemaLanguages] = useState([]);
    const { t } = useTranslation("project_setting/app_settings");
    const [saving, setSaving] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProject = async () => {
            setLoading(true);
            try {
                setProject(projectData); // Store project data in local state   
                setInitialProject(projectData); // Store initial project state for comparison

                // Map language codes to language objects
                const initialLanguages = projectData.schemaLanguages.map(langCode => {
                    const language = LanguagesMap.find(lang => lang.code === langCode);
                    return language || { code: langCode, name: langCode }; // Fallback for missing language
                });
                const schemaLanguages = [...initialLanguages, LanguagesMap.find(lang => lang.code === projectData.language)];
                setLanguage(projectData.language || initialLanguages[0]);
                setSchemaLanguages(schemaLanguages);
            } catch (error) {
                console.error("Error fetching project:", error);
                setError("Error fetching project");
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [projectData]);

    const handleSchemaLanguagesChange = (language) => {
        const isChecked = schemaLanguages.some(lang => lang.code === language.code);
        const updatedLanguages = isChecked
            ? schemaLanguages.filter(lang => lang.code !== language.code)
            : [...schemaLanguages, language];
        setSchemaLanguages(updatedLanguages);
    };

    // const handleLanguageChange = (selectedLanguage) => {
    //     setLanguage(selectedLanguage);
    //     setProject(prevProject => ({
    //         ...prevProject,
    //         language: selectedLanguage
    //     }));
    // };

    const hasChanges = () => {
        if (!initialProject) return false;
        const filteredLanguages = schemaLanguages.filter(lang => lang.code !== initialProject.language);
        const schemaLanguageCodes = filteredLanguages.map(lang => lang.code);
        return (
            initialProject.language !== language ||
            JSON.stringify(initialProject.schemaLanguages.filter(lang => lang !== initialProject.language).sort()) !== JSON.stringify(schemaLanguageCodes.sort())
        );
    };

    const onUpdate = async () => {
        if (!hasChanges()) return; // Prevent update if no changes

        setSaving(true);
        try {
            const previousSchemaLanguages = project.schemaLanguages.filter(lang => lang !== project.language);
            const schemaLanguageCodes = schemaLanguages.filter(lang => lang.code !== project.language).map(lang => lang.code);
            const newLanguages = schemaLanguageCodes.filter(lang => !previousSchemaLanguages.includes(lang));
            let updateResponse;
            if (newLanguages.length > 0) {
                updateResponse = await updateLabelsForSchema(project._id, {
                    schemaLanguages: newLanguages
                });
            }
            if (updateResponse.status === "Failure") {
                throw new Error("errors.failed_to_load_error");
            }
            const updatedProject = {
                language: language,
                schemaLanguages: schemaLanguageCodes,
            };
            try {
                await updateProject(project._id, updatedProject);
            } catch (e) {
                throw new Error("error.update_project");
            }
            updateProjectData({
                ...project,
                ...updatedProject
            }); // Update parent component state
            setProject({
                ...project,
                ...updatedProject
            }); // Update local state after successful update
            setInitialProject({
                ...project,
                ...updatedProject
            }); // Update initial state to reflect saved changes
        } catch (error) {
            console.error("Error updating project:", error);
            setError(t(error.message));
        } finally {
            setSaving(false);
            setShowSaveDialog(false);
        }
    };

    const getNewLanguages = () => {
        const previousSchemaLanguages = project.schemaLanguages;
        const filteredLanguages = schemaLanguages.filter(lang => lang.code !== project.language);
        const newLanguages = filteredLanguages
            .filter(lang => !previousSchemaLanguages.includes(lang.code))
            .map(lang => lang.name.split(' - ')[0]);
        return newLanguages.join(', ');
    };

    const shouldCheckboxBeDisabled = (dataItem) => {
        const isLanguageSelected = schemaLanguages.some(lang => lang.code === dataItem.code);
        const isDisabled = initialProject.schemaLanguages.includes(dataItem.code) ||
            initialProject.language === dataItem.code ||
            (!isLanguageSelected && subscription.numOfLanguages <= schemaLanguages.length);
        return { isDisabled, isLanguageSelected };
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <PageContainer>
            <Header header={t("header.title")} showAction={false} />
            <Scrollable>
                {saving && <OverlayLoader />}
                {
                    error && (
                        <Dialog
                            title={" "}
                            height={"75%"}
                            width={"60%"}
                            onClose={() => { setError(null) }}
                        >
                            <FailedToLoadError
                                error={error}
                            />
                        </Dialog>
                    )
                }
                {showSaveDialog &&
                    <SaveDialog
                        showLoader={saving}
                        Title={t("save_dialog.title")}
                        message={t("save_dialog.message")}
                        message2={t("save_dialog.message2", { languages: getNewLanguages() })}
                        onSave={onUpdate}
                        onCancel={(e) => setShowSaveDialog(false)}
                        toggleSaveDialog={(e) => setShowSaveDialog(!showSaveDialog)}
                        SaveButtonText={t("save_dialog.buttons.confirm")}
                        cancelButtonText={t("save_dialog.buttons.cancel")}
                    />
                }
                <div className="container-fluid">
                    <div className='row gx-5'>
                        <div className='col-12 mt-2'>
                            <div className='form-group'>
                                <Grid
                                    data={LanguagesMap}
                                >
                                    <GridColumn field="name" title={t("table.header.language")} />
                                    {/* <GridColumn field="code" title={t("table.header.locale")} /> */}
                                    {/* <GridColumn
                                        title={t("table.header.default")}
                                        cell={(props) => (
                                            <td>
                                                <Checkbox
                                                    checked={project.language === props.dataItem.code}
                                                    onChange={() => handleLanguageChange(props.dataItem.code)}
                                                />
                                            </td>
                                        )}
                                    /> */}
                                    <GridColumn
                                        cell={(props) => {
                                            const { isDisabled, isLanguageSelected } = shouldCheckboxBeDisabled(props.dataItem);
                                            return (
                                                <td className="ps-4">
                                                    <Checkbox
                                                        disabled={isDisabled}
                                                        checked={isLanguageSelected}
                                                        onChange={() => handleSchemaLanguagesChange(props.dataItem)}
                                                    />
                                                </td>
                                            );
                                        }}
                                    />
                                </Grid>
                            </div>
                        </div>
                        <div className='col-12 mt-4'>
                            <button
                                onClick={() => setShowSaveDialog(true)}
                                className='btn btn-primary me-2'
                                disabled={!hasChanges() || saving}>
                                {t("form.buttons.save_label")}
                            </button>
                        </div>
                    </div>
                </div>
            </Scrollable>
        </PageContainer>
    );
};

export default AppSettings;
